/**
 * What a pad is, and what shape it makes on the board.
 *
 * **One function turns a pad into geometry.** Twelve files in this program
 * used to work that out for themselves, and the cost of that has been paid
 * repeatedly: arcs drifted between the renderer and the plotter twice, and a
 * measurement that took a pad's extent one way and an aperture's another sent
 * a day's work chasing a fault that was not there. A richer pad model with
 * twelve independent readings would multiply that rather than fix it, so
 * everything — drawing, plotting, clearance, hit-testing, export — comes
 * through {@link padOutline}.
 *
 * **Shape and drilling are separate questions.** CIRCAD's type code answers
 * both at once (`T2` is a rectangular through pad), which is exactly what
 * forced an imported pad to be squashed into one of three shapes the moment it
 * was read. A pad now carries `through` and `shape` of its own; the type code
 * is kept for the file format and consulted only when they are absent, so
 * every file and library part written before this reads as it always did.
 */

import {
  padIsRect, padIsThrough,
  type PadEntity, type PadShape, type Vertex,
} from '../model.ts';

/** Whether the pad is drilled through the board. */
export function padThrough(p: PadEntity): boolean {
  return p.through ?? padIsThrough(p.type);
}

/**
 * The pad's shape, from the type code when it has none of its own.
 *
 * The reduction is the same one CIRCAD makes: a rectangular code is a
 * rectangle, and anything else is round when it is square and a stadium when
 * it is not — which is what an oval pad means in that vocabulary.
 */
export function padShape(p: PadEntity): PadShape {
  if (p.shape) return p.shape;
  if (padIsRect(p.type)) return { kind: 'rect' };
  return Math.abs(p.sizeX - p.sizeY) < 1e-9 ? { kind: 'round' } : { kind: 'obround' };
}

/** Points per quarter turn when a curve has to be drawn as a polygon. */
const ARC_STEPS = 8;

/**
 * The pad's outline in world coordinates, rotation included.
 *
 * `grow` swells the outline by that much on every side — the mask rule wants a
 * pad slightly larger, a stencil slightly smaller — and is applied to the
 * shape itself rather than to its bounding box, so a rounded pad stays rounded
 * when it grows.
 */
export function padOutline(p: PadEntity, grow = 0): Vertex[] {
  const shape = padShape(p);
  const w = Math.max(0, p.sizeX + grow * 2) / 2;
  const h = Math.max(0, p.sizeY + grow * 2) / 2;
  if (w <= 0 || h <= 0) return [];

  const local = localOutline(shape, w, h, grow);
  const rot = ((p.rotation ?? 0) * Math.PI) / 180;
  if (rot === 0) return local.map((v) => ({ x: p.x + v.x, y: p.y + v.y }));

  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return local.map((v) => ({
    x: p.x + v.x * cos - v.y * sin,
    y: p.y + v.x * sin + v.y * cos,
  }));
}

/** The outline about the origin, before the pad is turned or placed. */
function localOutline(shape: PadShape, w: number, h: number, grow: number): Vertex[] {
  switch (shape.kind) {
    case 'round':
      return ellipse(w, h);

    case 'rect':
      return [{ x: -w, y: -h }, { x: w, y: -h }, { x: w, y: h }, { x: -w, y: h }];

    case 'obround': {
      // Semicircular ends on whichever axis is longer.
      const r = Math.min(w, h);
      const flat = Math.max(w, h) - r;
      const pts: Vertex[] = [];
      const horizontal = w > h;
      for (let i = 0; i <= ARC_STEPS * 2; i++) {
        const t = -Math.PI / 2 + (Math.PI * i) / (ARC_STEPS * 2);
        pts.push(horizontal
          ? { x: flat + r * Math.cos(t), y: r * Math.sin(t) }
          : { x: r * Math.sin(t), y: flat + r * Math.cos(t) });
      }
      for (let i = 0; i <= ARC_STEPS * 2; i++) {
        const t = Math.PI / 2 + (Math.PI * i) / (ARC_STEPS * 2);
        pts.push(horizontal
          ? { x: -flat + r * Math.cos(t), y: r * Math.sin(t) }
          : { x: r * Math.sin(t), y: -flat + r * Math.cos(t) });
      }
      return pts;
    }

    case 'roundrect': {
      // The corner grows with the pad, so a swelled mask opening keeps the
      // same corner as the copper rather than turning square.
      const r = Math.min(Math.max(0, shape.radius + grow), Math.min(w, h));
      if (r <= 0) return localOutline({ kind: 'rect' }, w, h, 0);
      return corners(w, h, (cx, cy, from) => {
        const out: Vertex[] = [];
        for (let i = 0; i <= ARC_STEPS; i++) {
          const t = from + (Math.PI / 2) * (i / ARC_STEPS);
          out.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
        }
        return out;
      }, r);
    }

    case 'chamfered': {
      const c = Math.min(Math.max(0, shape.chamfer), Math.min(w, h));
      if (c <= 0) return localOutline({ kind: 'rect' }, w, h, 0);
      return [
        { x: -w + c, y: -h }, { x: w - c, y: -h },
        { x: w, y: -h + c }, { x: w, y: h - c },
        { x: w - c, y: h }, { x: -w + c, y: h },
        { x: -w, y: h - c }, { x: -w, y: -h + c },
      ];
    }

    case 'trapezoid': {
      // One end wider than the other by `delta`, split either side of centre.
      const d = shape.delta / 2;
      return [
        { x: -w - d, y: -h }, { x: w + d, y: -h },
        { x: w - d, y: h }, { x: -w + d, y: h },
      ];
    }

    case 'polygon': {
      if (shape.outline.length < 3) return [];
      if (grow === 0) return shape.outline;
      // Swelling an arbitrary outline properly is an offsetting problem; a
      // scale about the centre is close enough for the small amounts a mask
      // or stencil rule asks for, and never produces self-intersection.
      const ext = extentOf(shape.outline);
      const sx = ext.w > 0 ? (ext.w + grow * 2) / ext.w : 1;
      const sy = ext.h > 0 ? (ext.h + grow * 2) / ext.h : 1;
      return shape.outline.map((v) => ({ x: v.x * sx, y: v.y * sy }));
    }
  }
}

/** Walk the four corners anticlockwise from bottom-left, inset by `r`. */
function corners(
  w: number, h: number,
  arc: (cx: number, cy: number, from: number) => Vertex[],
  r: number,
): Vertex[] {
  return [
    ...arc(w - r, h - r, 0),
    ...arc(-w + r, h - r, Math.PI / 2),
    ...arc(-w + r, -h + r, Math.PI),
    ...arc(w - r, -h + r, -Math.PI / 2),
  ];
}

function ellipse(w: number, h: number): Vertex[] {
  const n = ARC_STEPS * 4;
  const pts: Vertex[] = [];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    pts.push({ x: w * Math.cos(t), y: h * Math.sin(t) });
  }
  return pts;
}

function extentOf(pts: Vertex[]): { w: number; h: number } {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return { w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

/**
 * The CIRCAD type code a pad should be stored under.
 *
 * The one place the richer model is reduced to the older one. Everything that
 * cannot be said in three shapes becomes the nearest of them, and the true
 * shape travels separately in the record's payload.
 */
export function circadTypeFor(p: PadEntity): PadEntity['type'] {
  const through = padThrough(p);
  const shape = padShape(p);
  const rectish = shape.kind === 'rect' || shape.kind === 'roundrect'
    || shape.kind === 'chamfered' || shape.kind === 'trapezoid'
    || shape.kind === 'polygon';
  if (rectish) return through ? 'T2' : 'S2';
  // Round and stadium both land on the oval code; CIRCAD tells them apart by
  // whether the two sizes are equal, exactly as `padShape` reads them back.
  return through ? 'T1' : 'S1';
}

/**
 * Whether the pad has corners rather than being round-ended.
 *
 * The distinction the older code drew with `padIsRect`, widened to the shapes
 * that did not exist then: a rounded rectangle and a chamfered one are corner
 * shapes whose corners happen to be eased, and count as rectangles wherever
 * the difference is one of degree.
 */
export function padIsRectangular(p: PadEntity): boolean {
  const k = padShape(p).kind;
  return k !== 'round' && k !== 'obround';
}
